import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, MousePointerClick, CheckCircle2, XCircle, Download, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api, { API } from "@/lib/api";

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function presetToRange(preset) {
  const end = new Date();
  const start = new Date();
  if (preset === "7d") start.setDate(end.getDate() - 6);
  if (preset === "30d") start.setDate(end.getDate() - 29);
  return { startDate: toISODate(start), endDate: toISODate(end) };
}

const STAT_CARDS = [
  { key: "page_visitors", label: "Page Visitors", icon: Eye, color: "text-indigo-400" },
  { key: "buy_now_clicks", label: "Buy Now Clicks", icon: MousePointerClick, color: "text-amber-400" },
  { key: "successful_transactions", label: "Successful Transactions", icon: CheckCircle2, color: "text-emerald-400" },
  { key: "failed_transactions", label: "Failed Transactions", icon: XCircle, color: "text-rose-400" },
];

export default function PageAnalyticsPanel() {
  const [pages, setPages] = useState([]);
  const [pageSlug, setPageSlug] = useState("");
  const [preset, setPreset] = useState("7d");
  const [customStart, setCustomStart] = useState(toISODate(new Date(Date.now() - 6 * 86400000)));
  const [customEnd, setCustomEnd] = useState(toISODate(new Date()));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/page-analytics/pages").then((r) => {
      setPages(r.data);
      if (r.data.length > 0) setPageSlug((prev) => prev || r.data[0].page_slug);
    });
  }, []);

  const range = preset === "custom" ? { startDate: customStart, endDate: customEnd } : presetToRange(preset);

  useEffect(() => {
    if (!pageSlug) return;
    setLoading(true);
    api.get("/admin/page-analytics/summary", { params: { page_slug: pageSlug, start_date: range.startDate, end_date: range.endDate } })
      .then((r) => setSummary(r.data))
      .catch(() => toast.error("Could not load page analytics"))
      .finally(() => setLoading(false));
  }, [pageSlug, range.startDate, range.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadUrl = `${API}/admin/page-analytics/export?page_slug=${encodeURIComponent(pageSlug)}&start_date=${range.startDate}&end_date=${range.endDate}`;

  return (
    <div data-testid="admin-page-analytics-panel">
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <select value={pageSlug} onChange={(e) => setPageSlug(e.target.value)} data-testid="page-analytics-page-select"
          className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-200 min-w-[180px]">
          {pages.length === 0 && <option value="">No page data yet</option>}
          {pages.map((p) => (
            <option key={p.page_slug} value={p.page_slug}>{p.label}</option>
          ))}
        </select>

        <select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="page-analytics-date-preset"
          className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-200">
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="custom">Custom Range</option>
        </select>

        {preset === "custom" && (
          <>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="page-analytics-custom-start"
              className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-200" />
            <span className="text-xs text-slate-500">to</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="page-analytics-custom-end"
              className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-200" />
          </>
        )}

        <a href={downloadUrl} data-testid="page-analytics-download-report-btn"
          className="flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors ml-auto">
          <Download className="w-3.5 h-3.5" /> Download Report
        </a>
      </div>

      {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>}

      {!loading && summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {STAT_CARDS.map(({ key, label, icon: Icon, color }) => (
              <div key={key} className="glass-panel rounded-xl p-4" data-testid={`page-analytics-stat-${key}`}>
                <Icon className={`w-4 h-4 mb-2 ${color}`} />
                <p className="font-heading text-2xl font-bold text-white">{summary[key]}</p>
                <p className="text-xs text-slate-400">{label}</p>
              </div>
            ))}
          </div>

          <div className="glass-panel rounded-xl p-4" data-testid="page-analytics-chart">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={summary.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="page_visitors" name="Visitors" stroke="#818cf8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="buy_now_clicks" name="Buy Now Clicks" stroke="#fbbf24" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="successful_transactions" name="Success" stroke="#34d399" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="failed_transactions" name="Failed" stroke="#fb7185" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {!loading && !summary && (
        <p className="text-sm text-slate-400 text-center py-8" data-testid="page-analytics-empty">No tracked pages yet — data will appear as visitors browse the site.</p>
      )}
    </div>
  );
}
